/**
 * JOIN WORKSPACE (A06)
 *
 * Design ref: A06JoinWorkspace in screens-auth.jsx
 * Shows invite preview card if pending invite detected, then tabs for code/search.
 */

import React, { useState, useEffect, FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getBrandingConfig } from '@shared/config/brandingConfig';
import { useDynamicBranding } from '../hooks/useDynamicBranding';
import { supabase } from '@shared/lib/supabaseClient';
import { Button } from '@shared/ui/shadcn/ui/button';
import { Alert, AlertDescription } from '@shared/ui/shadcn/ui/alert';
import { Input } from '@shared/ui/shadcn/ui/input';
import { Loader2, KeyRound, Search, Building2, CheckCircle2 } from 'lucide-react';
import AuthLayout from '../components/AuthLayout';
import { LoginTabs, LoginTabsList, LoginTabsTrigger, LoginTabsContent } from '../components/LoginTabs';
import { handleHandoffRedirect, setSSOCookie } from './Login';
import { createSsoTrace } from '@shared/utils/ssoTrace';
import { notifyCoreAPILogin } from '@shared/services/coreAuthService';
import { APP_SSO_CONFIGS } from '@shared/navigation/appSSOConfig';

const getCoreApiUrl = (): string => {
  const envUrl = (import.meta.env.VITE_CORE_API_URL || '').trim();
  if (envUrl) return envUrl;
  return import.meta.env.DEV ? 'http://localhost:4000' : 'https://api.elfom.com';
};

interface PendingInvite {
  workspace_id: string;
  workspace_name: string;
  workspace_initials: string;
  role: string;
  invited_by: string;
}

interface SearchResult {
  id: string;
  nombre: string;
  tipo: string | null;
}

const JoinWorkspacePage: React.FC = () => {
  const [params] = useSearchParams();
  const app = params.get('app') || 'empresa';
  const returnTo = params.get('returnTo');
  const clientOriginParam = params.get('clientOrigin');

  useDynamicBranding(app);
  const branding = getBrandingConfig(app);

  const [tab, setTab] = useState<'code' | 'search'>('code');

  // Pending invite state
  const [pendingInvite, setPendingInvite] = useState<PendingInvite | null>(null);
  const [acceptingInvite, setAcceptingInvite] = useState(false);

  // Code tab state
  const [code, setCode] = useState('');
  const [codeLoading, setCodeLoading] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [codeSuccess, setCodeSuccess] = useState(false);

  // Search tab state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searchDone, setSearchDone] = useState(false);
  const [joiningId, setJoiningId] = useState<string | null>(null);

  // Check for pending invites on mount
  useEffect(() => {
    const checkPendingInvites = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) return;

        const coreApiUrl = getCoreApiUrl();
        const response = await fetch(`${coreApiUrl}/workspaces/pending-invites`, {
          headers: { 'Authorization': `Bearer ${session.access_token}` },
        });

        if (response.ok) {
          const data = await response.json();
          const invites = data.invites || data.pending || [];
          if (invites.length > 0) {
            const inv = invites[0];
            setPendingInvite({
              workspace_id: inv.workspace_id || inv.empresa_id,
              workspace_name: inv.workspace_name || inv.nombre || 'Workspace',
              workspace_initials: (inv.workspace_name || inv.nombre || 'WS')
                .split(' ')
                .map((w: string) => w[0])
                .join('')
                .slice(0, 2)
                .toUpperCase(),
              role: inv.role || inv.rol || 'Miembro',
              invited_by: inv.invited_by || inv.invitado_por || 'Admin',
            });
          }
        }
      } catch (_) {
        // silent - non-critical
      }
    };
    checkPendingInvites();
  }, []);

  const performHandoff = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return;

    try {
      await notifyCoreAPILogin(session.access_token);
      await setSSOCookie(session.access_token);
    } catch (_) {}

    if (app && APP_SSO_CONFIGS[app]) {
      const trace = createSsoTrace({ app: 'accounts', feature: 'SSO_HANDOFF' });
      trace.step('JOIN_WORKSPACE_SUCCESS', {});
      await handleHandoffRedirect(
        app,
        returnTo || '/dashboard',
        () => {},
        trace,
        clientOriginParam,
      );
    }
  };

  const handleAcceptInvite = async () => {
    if (!pendingInvite) return;
    setAcceptingInvite(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      const coreApiUrl = getCoreApiUrl();
      const response = await fetch(`${coreApiUrl}/workspaces/accept-invite`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ workspace_id: pendingInvite.workspace_id }),
      });

      if (response.ok) {
        await performHandoff();
      }
    } catch (_) {
      // silent
    } finally {
      setAcceptingInvite(false);
    }
  };

  const handleCodeSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setCodeError(null);

    const trimmed = code.trim().toUpperCase();
    if (trimmed.length < 6 || trimmed.length > 8) {
      setCodeError('El codigo debe tener entre 6 y 8 caracteres');
      return;
    }

    setCodeLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setCodeError('No hay sesion activa. Por favor inicia sesion primero.');
        setCodeLoading(false);
        return;
      }

      const coreApiUrl = getCoreApiUrl();
      const response = await fetch(`${coreApiUrl}/workspaces/join`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code: trimmed }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
        setCodeError(errorData.message || errorData.error || 'Codigo invalido o expirado');
        setCodeLoading(false);
        return;
      }

      setCodeSuccess(true);
      await performHandoff();
    } catch (err: any) {
      setCodeError(err?.message || 'Error de conexion. Intenta de nuevo.');
    } finally {
      setCodeLoading(false);
    }
  };

  const handleSearch = async (e: FormEvent) => {
    e.preventDefault();
    setSearchError(null);
    setSearchResults([]);
    setSearchDone(false);

    if (!searchQuery.trim()) {
      setSearchError('Ingresa un nombre para buscar');
      return;
    }

    setSearchLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setSearchError('No hay sesion activa.');
        setSearchLoading(false);
        return;
      }

      const coreApiUrl = getCoreApiUrl();
      const response = await fetch(
        `${coreApiUrl}/workspaces/search?q=${encodeURIComponent(searchQuery.trim())}`,
        { headers: { 'Authorization': `Bearer ${session.access_token}` } },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error' }));
        setSearchError(errorData.message || errorData.error || 'Error en la busqueda');
        setSearchLoading(false);
        return;
      }

      const data = await response.json();
      setSearchResults(data.workspaces || data.results || []);
      setSearchDone(true);
    } catch (err: any) {
      setSearchError(err?.message || 'Error de conexion.');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleJoinFromSearch = async (workspaceId: string) => {
    setSearchError(null);
    setJoiningId(workspaceId);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setSearchError('No hay sesion activa.');
        setJoiningId(null);
        return;
      }

      const coreApiUrl = getCoreApiUrl();
      const response = await fetch(`${coreApiUrl}/workspaces/join`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ workspace_id: workspaceId }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error' }));
        setSearchError(errorData.message || errorData.error || 'No se pudo unir al workspace');
        setJoiningId(null);
        return;
      }

      await performHandoff();
    } catch (err: any) {
      setSearchError(err?.message || 'Error de conexion.');
    } finally {
      setJoiningId(null);
    }
  };

  return (
    <AuthLayout>
      <div className="w-full flex flex-col gap-5" style={{ maxWidth: 480 }}>
        {/* Header */}
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-foreground">
            Unirse a un workspace
          </h1>
          <p className="text-sm text-muted-foreground mt-1" style={{ lineHeight: 1.55 }}>
            Ingresa tu codigo de invitacion o busca por nombre
          </p>
        </div>

        {/* Pending invite card (shown above tabs if detected) */}
        {pendingInvite && (
          <div className="rounded-md border border-border bg-card p-4">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">
              Invitacion pendiente
            </p>
            <div className="grid gap-3.5" style={{ gridTemplateColumns: '44px 1fr auto', alignItems: 'center' }}>
              {/* Workspace mark */}
              <div className="w-11 h-11 rounded-lg bg-foreground text-background flex items-center justify-center font-mono text-sm font-bold">
                {pendingInvite.workspace_initials}
              </div>
              {/* Info */}
              <div className="min-w-0">
                <p className="font-semibold text-sm text-foreground truncate">
                  {pendingInvite.workspace_name}
                </p>
                <p className="text-[10.5px] font-mono uppercase tracking-wide text-primary mt-0.5">
                  {pendingInvite.role}
                </p>
              </div>
              {/* Invited by */}
              <div className="text-right">
                <p className="text-[10.5px] font-mono uppercase tracking-wide text-muted-foreground">
                  Invitado por
                </p>
                <p className="text-xs font-semibold text-foreground">
                  {pendingInvite.invited_by}
                </p>
              </div>
            </div>
            <Button
              onClick={handleAcceptInvite}
              disabled={acceptingInvite}
              className="w-full h-11 mt-4"
              size="lg"
            >
              {acceptingInvite ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Aceptando...
                </>
              ) : (
                'Aceptar y unirme'
              )}
            </Button>
          </div>
        )}

        {/* Tabs */}
        <LoginTabs
          value={tab}
          onValueChange={(v) => setTab(v as 'code' | 'search')}
        >
          <LoginTabsList className="!grid-cols-2">
            <LoginTabsTrigger value="code">
              <KeyRound className="h-3.5 w-3.5 mr-1.5" />
              Tengo un codigo
            </LoginTabsTrigger>
            <LoginTabsTrigger value="search">
              <Search className="h-3.5 w-3.5 mr-1.5" />
              Buscar por nombre
            </LoginTabsTrigger>
          </LoginTabsList>

          {/* Tab: Code */}
          <LoginTabsContent value="code" className="mt-0">
            <form onSubmit={handleCodeSubmit} className="space-y-4">
              {codeError && (
                <Alert variant="destructive">
                  <AlertDescription>{codeError}</AlertDescription>
                </Alert>
              )}
              {codeSuccess && (
                <Alert variant="default" className="bg-green-50 border-green-200 text-green-800">
                  <AlertDescription className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4" />
                    Te has unido exitosamente. Redirigiendo...
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-1.5">
                <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Codigo de invitacion
                </label>
                <Input
                  id="invitation-code"
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/[^A-Za-z0-9]/g, '').slice(0, 8).toUpperCase())}
                  placeholder="Ej: AB12CD34"
                  maxLength={8}
                  disabled={codeLoading || codeSuccess}
                  className="h-12 text-lg font-mono uppercase tracking-widest text-center"
                />
                <p className="text-[11.5px] text-muted-foreground text-center">
                  Codigo de 6-8 caracteres proporcionado por el administrador
                </p>
              </div>

              <Button
                type="submit"
                disabled={codeLoading || codeSuccess || code.trim().length < 6}
                className="w-full h-11"
                size="lg"
              >
                {codeLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Validando...
                  </>
                ) : codeSuccess ? (
                  'Unido exitosamente'
                ) : (
                  'Verificar y continuar'
                )}
              </Button>
            </form>
          </LoginTabsContent>

          {/* Tab: Search */}
          <LoginTabsContent value="search" className="mt-0">
            <div className="space-y-4">
              {searchError && (
                <Alert variant="destructive">
                  <AlertDescription>{searchError}</AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleSearch} className="space-y-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Nombre del workspace
                  </label>
                  <Input
                    id="search-workspace"
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Ej: Tapiceria Reyna"
                    disabled={searchLoading}
                    className="h-10 text-sm"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={searchLoading || !searchQuery.trim()}
                  className="w-full h-11"
                  size="lg"
                >
                  {searchLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Buscando...
                    </>
                  ) : (
                    'Buscar'
                  )}
                </Button>
              </form>

              {/* Results */}
              {searchDone && searchResults.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No se encontraron workspaces con ese nombre.
                </p>
              )}

              {searchResults.length > 0 && (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {searchResults.map((ws) => (
                    <div
                      key={ws.id}
                      className="flex items-center justify-between gap-3 rounded-md border border-border p-3"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <Building2 className="h-5 w-5 text-muted-foreground shrink-0" />
                        <div className="min-w-0">
                          <p className="font-medium text-sm text-foreground truncate">
                            {ws.nombre}
                          </p>
                          {ws.tipo && (
                            <p className="text-xs text-muted-foreground truncate">
                              {ws.tipo}
                            </p>
                          )}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={joiningId === ws.id}
                        onClick={() => handleJoinFromSearch(ws.id)}
                      >
                        {joiningId === ws.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          'Unirse'
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </LoginTabsContent>
        </LoginTabs>

        {/* Footer */}
        <div className="text-center text-xs text-muted-foreground">
          <a
            href={`/choose-path?app=${app}${returnTo ? `&returnTo=${encodeURIComponent(returnTo)}` : ''}`}
            className="text-primary hover:underline font-medium"
          >
            ← Volver
          </a>
        </div>
      </div>
    </AuthLayout>
  );
};

export default JoinWorkspacePage;
